import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/** A page or panel whose code is a chunk of its own, and what loads it ahead. */
export type LazyView = LazyExoticComponent<ComponentType> & { preload: () => Promise<unknown> };

type Loaded = { default: ComponentType };

const RELOADED = "lazyView:reloaded";

/** Whether this tab may reload for code a newer release replaced: once, until
 *  a view loads again, so a reload that does not help shows the failure. */
function mayReload(): boolean {
  try {
    if (sessionStorage.getItem(RELOADED)) return false;
    sessionStorage.setItem(RELOADED, "1");
    return true;
  } catch {
    return false;
  }
}

function loadedAgain(): void {
  try {
    sessionStorage.removeItem(RELOADED);
  } catch {
    // Without storage there is no reload to undo.
  }
}

/**
 * A page or panel whose code loads on its first render, or ahead through
 * `preload`. One loaded ahead renders at once: React suspends only on a
 * thenable still pending once it has called `then`, and this one settles
 * inside it. A render whose code is gone, as after a deploy in a tab opened
 * before it, reloads the page once to fetch the release's own files.
 */
export function lazyView(load: () => Promise<ComponentType>): LazyView {
  let loaded: Loaded | undefined;
  let loading: Promise<Loaded> | undefined;
  const preload = () =>
    (loading ??= load().then(
      (view) => {
        loadedAgain();
        return (loaded = { default: view });
      },
      (error: unknown) => {
        loading = undefined;
        throw error;
      },
    ));
  const view = lazy(() => {
    const ready = loaded;
    if (!ready)
      return preload().catch((error: unknown) => {
        if (!mayReload()) throw error;
        location.reload();
        return new Promise<Loaded>(() => {});
      });
    return { then: (settle: (view: Loaded) => void) => settle(ready) } as Promise<Loaded>;
  });
  return Object.assign(view, { preload });
}
